//import { StationDTO } from '@/api/stations/dto/StationDTO'
import { TimeSeriesDTO } from '@/api/stations/dto/TimeSeriesDTO'
import { StationAPI } from '@/api/stations/StationAPI'
import { SettingAPI } from '@/api/setting/SettingAPI'
import { EventEmitter } from '@/model/EventEmitter'
import { Station } from '@/model/Station'
import { TimeSeries } from '@/model/TimeSeries'
import { WaterStationLegend } from '@/model/WaterStationLegend'
import { LegendFactory } from '@/services/setting/LegendFactory'
import { Feature, FeatureCollection } from '@turf/turf'
import { GeoJsonProperties, Geometry } from 'geojson'
import { DateTime } from 'luxon'
import { IStationService } from './IStationService'
import { SelectionEventArgs } from './shared/SelectionManager'
import { FeatureMapper } from './shared/FeatureMapper'
import { BaseStationService } from './BaseStationService'
import { ServiceLocator } from '../ServiceLocator'

//import Stations from './mock/dijle_stations.json'

import { StationFactory } from './StationFactory'
import { TimeSeriesFactory } from './TimeSeriesFactory'

export interface StationWithData {
  station: Station
  hasData: boolean
}

export class StationService extends BaseStationService<Station> implements IStationService {
  private api: StationAPI = new StationAPI()
  private settingAPI: SettingAPI = new SettingAPI()
  private waterLegend: WaterStationLegend | null = null

  constructor(serviceLocator: ServiceLocator = {} as ServiceLocator) {
    super(serviceLocator)
  }

  readonly stationSelectionChanged: EventEmitter<SelectionEventArgs<Station>> = this.selectionChanged

  async toggleStation(station: Station): Promise<void> {
    this.toggleEntity(station.no, station)
  }

  clearStations(): void {
    this.clearAllSelections()
  }

  async getStationsOfCity(cityName: string, timestamp: DateTime): Promise<Station[]> {
    try {
      const stationsDTO = await this.api.getStationsOfCity(cityName, timestamp)
      return Promise.resolve(StationFactory.fromDTO(stationsDTO))
    } catch (e) {
      return Promise.resolve([])
    }
  }

  async getTimeSeriesForStation(station: Station, timestamp: DateTime): Promise<TimeSeries[]> {
    const idsToDTO = await this.api.getTimeseriesForStationByTimestamp(station.id, timestamp.toUTC())
    return [TimeSeriesFactory.newFromDTO(station, Array.from(idsToDTO.values()))]
  }

  async getStationsWithData(cityName: string, timestamp: DateTime): Promise<StationWithData[]> {
    const stations = await this.getStationsOfCity(cityName, timestamp)

    // Fetch timeseries for all stations in parallel
    const stationsWithData = await Promise.all(
      stations.map(async (station) => {
        try {
          const timeseriesMap = await this.api.getTimeseriesForStationByTimestamp(station.id, timestamp.toUTC())
          const hasData = this.checkHasData(timeseriesMap)
          return { station, hasData }
        } catch {
          return { station, hasData: false }
        }
      }),
    )

    return stationsWithData
  }

  private checkHasData(timeseriesMap: Map<string, TimeSeriesDTO>): boolean {
    for (const timeseries of timeseriesMap.values()) {
      if (timeseries.history && timeseries.history.length > 0) {
        return true
      }
    }
    return false
  }

  async getWaterLegend(): Promise<WaterStationLegend> {
    if (this.waterLegend == null) {
      const settingsRaw = await this.settingAPI.getRawSettings()
      if (settingsRaw.legends.stations?.water) {
        this.waterLegend = LegendFactory.waterStationLegendFromDTO(settingsRaw.legends.stations.water)
      } else {
        // Fallback: create default legend if not in settings
        this.waterLegend = LegendFactory.waterStationLegendFromDTO([
          { state: 'nodata_unselected', color: '#808080', labelText: 'No data' },
          { state: 'nodata_selected', color: '#606060', labelText: 'No data (selected)' },
          { state: 'data_unselected', color: '#4682B4', labelText: 'Data available' },
          { state: 'data_selected', color: '#7753EB', labelText: 'Data available (selected)' },
        ])
      }
    }

    return this.waterLegend
  }

  mapToFeatureCollection(stations: Station[]): FeatureCollection<Geometry, GeoJsonProperties> {
    return FeatureMapper.toFeatureCollection(stations, (s) => this.mapToFeature(s))
  }

  mapToFeatureCollectionWithColors(
    stationsWithData: StationWithData[],
    selectedIds: Set<number>,
    waterLegend: WaterStationLegend,
  ): FeatureCollection<Geometry, GeoJsonProperties> {
    return FeatureMapper.toFeatureCollection(stationsWithData, ({ station, hasData }) =>
      this.mapToFeatureWithColor(station, hasData, selectedIds.has(station.no), waterLegend),
    )
  }

  private mapToFeature(s: Station): Feature<Geometry, GeoJsonProperties> {
    return {
      type: 'Feature',
      id: s.no,
      geometry: {
        type: 'Point',
        coordinates: s.getLngLat(),
      },
      properties: {
        name_no: `${s.name}`,
        no: s.no,
        types: s.stationTypes,
      },
    }
  }

  private mapToFeatureWithColor(
    station: Station,
    hasData: boolean,
    isSelected: boolean,
    waterLegend: WaterStationLegend,
  ): Feature<Geometry, GeoJsonProperties> {
    return {
      type: 'Feature',
      id: station.no,
      geometry: {
        type: 'Point',
        coordinates: station.getLngLat(),
      },
      properties: {
        name_no: `${station.name}`,
        no: station.no,
        types: station.stationTypes,
        color: waterLegend.getColorByState(hasData, isSelected),
        hasData,
      },
    }
  }
}
